import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Calendar, MapPin, FileText, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import api from '../../api/apiClient';

interface Property {
  id: number;
  address: string;
  leaseId?: number;
  tenantName?: string;
}

const INSPECTION_TYPES = [
  { value: 'ROUTINE', label: 'Routine Inspection', description: 'Regular periodic inspection (typically every 3-6 months)' },
  { value: 'ENTRY', label: 'Entry Inspection', description: 'Inspection at the start of a new tenancy' },
  { value: 'EXIT', label: 'Exit Inspection', description: 'Inspection at the end of a tenancy' },
  { value: 'MAINTENANCE', label: 'Maintenance Inspection', description: 'Inspection to assess maintenance or repair needs' },
];

export default function CreateInspection() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    propertyId: '',
    leaseId: '',
    inspectionType: 'ROUTINE',
    scheduledDate: '',
    scheduledTime: '10:00',
    notes: '',
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/properties');
      const props = (response || []).map((p: any) => ({
        id: p.id,
        address: p.address,
        leaseId: p.currentLeaseId || p.leaseId,
        tenantName: p.tenantName,
      }));
      setProperties(props);
    } catch (err) {
      console.error('Failed to load properties:', err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.propertyId) {
      setError('Please select a property');
      return;
    }
    if (!form.scheduledDate) {
      setError('Please select a date');
      return;
    }

    setSaving(true);
    try {
      const scheduledAt = new Date(`${form.scheduledDate}T${form.scheduledTime}:00`).toISOString();

      const selectedProperty = properties.find(p => p.id === parseInt(form.propertyId));

      await api.post('/inspections', {
        propertyId: parseInt(form.propertyId),
        leaseId: selectedProperty?.leaseId || null,
        scheduledAt,
        inspectionType: form.inspectionType,
        notes: form.notes,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/agent/inspections');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to schedule inspection');
    } finally {
      setSaving(false);
    }
  };

  const selectedProperty = properties.find(p => p.id === parseInt(form.propertyId));

  // Set minimum date to today
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Inspection Scheduled!</h2>
          <p className="text-gray-600">The tenant will be notified about the upcoming inspection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/agent/inspections')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Schedule Inspection</h1>
            <p className="text-gray-600">Create a new property inspection</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Property Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Property</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Property *</label>
            <select
              value={form.propertyId}
              onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              required
            >
              <option value="">Choose a property...</option>
              {properties.map(property => (
                <option key={property.id} value={property.id}>
                  {property.address} {property.tenantName ? `(${property.tenantName})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedProperty && selectedProperty.tenantName && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Current Tenant:</strong> {selectedProperty.tenantName}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                The tenant will receive a notification about this inspection.
              </p>
            </div>
          )}
        </div>

        {/* Inspection Type */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Inspection Type</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {INSPECTION_TYPES.map(type => (
              <label
                key={type.value}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  form.inspectionType === type.value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="inspectionType"
                  value={type.value}
                  checked={form.inspectionType === type.value}
                  onChange={(e) => setForm({ ...form, inspectionType: e.target.value })}
                  className="sr-only"
                />
                <p className="font-medium text-gray-900">{type.label}</p>
                <p className="text-sm text-gray-500 mt-1">{type.description}</p>
              </label>
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                value={form.scheduledDate}
                onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
                min={today}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
              <select
                value={form.scheduledTime}
                onChange={(e) => setForm({ ...form, scheduledTime: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              >
                <option value="08:00">8:00 AM</option>
                <option value="08:30">8:30 AM</option>
                <option value="09:00">9:00 AM</option>
                <option value="09:30">9:30 AM</option>
                <option value="10:00">10:00 AM</option>
                <option value="10:30">10:30 AM</option>
                <option value="11:00">11:00 AM</option>
                <option value="11:30">11:30 AM</option>
                <option value="12:00">12:00 PM</option>
                <option value="12:30">12:30 PM</option>
                <option value="13:00">1:00 PM</option>
                <option value="13:30">1:30 PM</option>
                <option value="14:00">2:00 PM</option>
                <option value="14:30">2:30 PM</option>
                <option value="15:00">3:00 PM</option>
                <option value="15:30">3:30 PM</option>
                <option value="16:00">4:00 PM</option>
                <option value="16:30">4:30 PM</option>
                <option value="17:00">5:00 PM</option>
              </select>
            </div>
          </div>

          <p className="text-sm text-gray-500 mt-3">
            Note: Tenants must be given at least 7 days notice for routine inspections in most states.
          </p>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes (Optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder="Any special instructions or areas of focus for this inspection..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/agent/inspections')}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 font-medium"
          >
            {saving ? 'Scheduling...' : 'Schedule Inspection'}
          </button>
        </div>
      </form>
    </div>
  );
}

